// ONE poll for every project card on the page.
//
// Each card asks useAccLink(projectId); the ids of all mounted cards go out in a single
// GET /acc/links?projects=... - every 5 s while any linked project is queued or
// processing on the server (so the spinner and the ETA move), every 60 s otherwise (a
// newer ACC version queued by the server's own poll still shows up within a minute).
import { useEffect, useState } from "react";
import { getAccLinks } from "./accApi";

const FAST_MS = 5000;
const SLOW_MS = 60000;

const watchers = new Map(); // projectId -> Set(setState)
const links = new Map(); // projectId -> link view | null
let timer = null;
let inFlight = false;

const busy = (l) => !!(l && l.run && (l.run.status === "queued" || l.run.status === "running"));

function schedule(ms) {
  clearTimeout(timer);
  timer = watchers.size ? setTimeout(poll, ms) : null;
}

function publish() {
  for (const [id, sets] of watchers) {
    const v = links.has(id) ? links.get(id) : null;
    for (const set of sets) set(v);
  }
}

async function poll() {
  if (inFlight || !watchers.size) return;
  inFlight = true;
  const ids = [...watchers.keys()];
  try {
    const rows = (await getAccLinks(ids)) || [];
    const seen = new Set();
    for (const r of rows) {
      links.set(String(r.project), r);
      seen.add(String(r.project));
    }
    for (const id of ids) if (!seen.has(id)) links.set(id, null);
    publish();
  } catch (e) {
    // a failed poll keeps the last state; the next one tries again
  } finally {
    inFlight = false;
    // a card that mounted during this poll is answered at once, not in a minute
    const unasked = [...watchers.keys()].some((id) => !links.has(id));
    schedule(unasked ? 100 : [...links.values()].some(busy) ? FAST_MS : SLOW_MS);
  }
}

/** Ask for a poll now (after linking, syncing or unlinking). */
export function refreshAccLinks() {
  clearTimeout(timer);
  setTimeout(poll, 50);
}

/** Put a fresh server answer straight into the store (the link / sync responses carry one). */
export function setAccLink(projectId, view) {
  links.set(String(projectId), view || null);
  publish();
  schedule(busy(view) ? FAST_MS : SLOW_MS);
}

export function useAccLink(projectId) {
  const id = String(projectId || "");
  const [state, setState] = useState(links.has(id) ? links.get(id) : undefined);
  useEffect(() => {
    if (!id) return undefined;
    if (!watchers.has(id)) watchers.set(id, new Set());
    watchers.get(id).add(setState);
    // cards mounting together share one poll 100 ms after the first of them
    if (!links.has(id) && !inFlight) schedule(100);
    else if (!timer && !inFlight) schedule(100);
    return () => {
      const s = watchers.get(id);
      if (s) {
        s.delete(setState);
        if (!s.size) watchers.delete(id);
      }
      if (!watchers.size) schedule(0);
    };
  }, [id]);
  return state; // undefined = not asked yet, null = not linked, else the link view
}

export const isAccBusy = busy;
